import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wand2, Loader2, Zap, CheckCircle2, AlertTriangle, PlayCircle, Save, Sparkles } from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

function Card({ children, className = "" }) {
  return <div className={`bg-[#111827] border border-slate-800/80 rounded-xl p-5 ${className}`}>{children}</div>;
}

export default function DirectorSettings() {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const [custom, setCustom] = useState("");
  const [astra, setAstra] = useState({ endpoint: "", model_id: "", api_key: "" });

  const load = async () => {
    setError(null);
    try { const { data } = await api.get("/director"); setCfg(data); setAstra((a) => ({ ...a, endpoint: data.astra?.endpoint || "", model_id: data.astra?.model_id || "" })); }
    catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const model_id = custom.trim() || cfg.model_id;
      await api.put("/director", { provider: cfg.provider, model_id, quality: cfg.quality,
        auto_fallback: cfg.auto_fallback, fallback_order: cfg.fallback_order,
        max_revision_cycles: cfg.max_revision_cycles ?? 2 });
      toast.success("Director settings saved"); setCustom(""); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const runTest = async () => {
    setBusy(true); setTest(null);
    try { const { data } = await api.post("/director/test"); setTest(data);
      toast[data.fallback_used ? "warning" : "success"](`Director used ${data.model_used} (${data.latency_ms}ms)`);
    } catch (e) { toast.error(apiError(e)); setTest({ error: apiError(e) }); } finally { setBusy(false); }
  };
  const saveAstra = async () => {
    setBusy(true);
    try { await api.put("/director/astra", astra); setAstra((a) => ({ ...a, api_key: "" })); toast.success("Astra config saved"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const verifyAstra = async () => {
    setBusy(true);
    try { const { data } = await api.post("/director/astra/verify"); data.ok ? toast.success(`Astra verified (${data.latency_ms}ms)`) : toast.error(`Astra unverified: ${(data.error || "").slice(0, 120)}`); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!cfg) return <LoadingState label="Loading Director…" />;

  const candidates = cfg.director_candidates || [];
  const astraState = cfg.astra?.state || "unverified";

  return (
    <div className="animate-in-up max-w-4xl">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1 flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" /> Workspace</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Director Settings</h1>
        <p className="text-sm text-slate-400 mt-2">The Director is the top-level model that plans and orchestrates. It resolves through the registry with logged, never-silent fallback.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-4">Director slot</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Model (director-capable, from registry)</Label>
              <Select value={custom ? "__custom__" : cfg.model_id} onValueChange={(v) => { if (v === "__custom__") setCustom(cfg.model_id); else { setCustom(""); setCfg({ ...cfg, model_id: v }); } }}>
                <SelectTrigger className={inputCls} data-testid="director-model"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  {candidates.map((c) => <SelectItem key={c.model_id} value={c.model_id} disabled={!c.usable}>{c.name}{c.usable ? "" : " (unavailable)"}</SelectItem>)}
                  <SelectItem value="__custom__">Custom model id (future / Astra)…</SelectItem>
                </SelectContent>
              </Select>
              {custom !== "" && <Input className={`${inputCls} mt-2`} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. gpt-6-astra" data-testid="director-custom-model" />}
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Quality preference</Label>
              <Select value={cfg.quality} onValueChange={(v) => setCfg({ ...cfg, quality: v })}>
                <SelectTrigger className={inputCls} data-testid="director-quality"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  {["fast", "balanced", "best"].map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Fallback order (comma of model ids)</Label>
              <Input className={inputCls} value={(cfg.fallback_order || []).join(", ")} onChange={(e) => setCfg({ ...cfg, fallback_order: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid="director-fallback" />
            </div>
            <label className="flex items-center justify-between text-sm text-slate-300">
              <span>Auto-fallback allowed (every fallback is logged &amp; surfaced)</span>
              <Switch checked={cfg.auto_fallback} onCheckedChange={(v) => setCfg({ ...cfg, auto_fallback: v })} data-testid="director-autofallback" />
            </label>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Max autopilot revision cycles (quality gate)</Label>
              <Input type="number" min="0" max="5" className={inputCls} value={cfg.max_revision_cycles ?? 2}
                onChange={(e) => setCfg({ ...cfg, max_revision_cycles: parseInt(e.target.value || "0", 10) })} data-testid="director-max-cycles" />
              <p className="text-[11px] text-slate-600 mt-1">How many bounded auto-correction passes the Autopilot quality gate may run before it stops and hands off to human review.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="director-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>
              <Button onClick={runTest} disabled={busy} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="director-test"><PlayCircle className="w-4 h-4 mr-1.5" /> Test Director</Button>
            </div>
            {cfg.resolved?.error && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="director-resolve-error">{cfg.resolved.error}</div>}
            {test && (test.error
              ? <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="director-test-result">{test.error}</div>
              : <div className={`text-sm rounded-lg px-3 py-2 border ${test.fallback_used ? "bg-amber-500/10 text-amber-200 border-amber-500/30" : "bg-emerald-500/10 text-emerald-200 border-emerald-500/30"}`} data-testid="director-test-result">
                  <div className="flex items-center gap-1.5"><Zap className="w-4 h-4" /> Used <b>{test.model_used}</b> · {test.latency_ms}ms · ${test.cost}</div>
                  {test.fallback_used && <div className="text-xs mt-1">Requested "{test.requested_model}" was unavailable — fell back (logged).</div>}
                  <div className="text-xs mt-1 text-slate-400">"{test.reply}"</div>
                </div>)}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-semibold text-slate-100 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-300" /> GPT-6 Astra (future)</h3>
            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${astraState === "available" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`} data-testid="astra-state">
              {astraState === "available" ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              {astraState === "available" ? "Verified" : "Unverified — credentials/access required"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-3">Astra is not a currently-available API. Enter access details for when it launches; switching is one click after it verifies.</p>
          <div className="space-y-3">
            <div><Label className="text-slate-300 text-sm mb-1 block">Endpoint (OpenAI-compatible base URL)</Label><Input className={inputCls} value={astra.endpoint} onChange={(e) => setAstra({ ...astra, endpoint: e.target.value })} placeholder="https://api.astra.example/v1" data-testid="astra-endpoint" /></div>
            <div><Label className="text-slate-300 text-sm mb-1 block">Model id</Label><Input className={inputCls} value={astra.model_id} onChange={(e) => setAstra({ ...astra, model_id: e.target.value })} placeholder="gpt-6-astra" data-testid="astra-model" /></div>
            <div><Label className="text-slate-300 text-sm mb-1 block">API key {cfg.astra?.key_masked && <span className="text-slate-500 font-mono">(stored: {cfg.astra.key_masked})</span>}</Label><Input type="password" className={inputCls} value={astra.api_key} onChange={(e) => setAstra({ ...astra, api_key: e.target.value })} placeholder={cfg.astra?.key_masked ? "Replace key…" : "sk-…"} data-testid="astra-key" /></div>
            {cfg.astra?.last_error && <p className="text-xs text-rose-400/80 break-all">{cfg.astra.last_error}</p>}
            <div className="flex items-center gap-2">
              <Button onClick={saveAstra} disabled={busy} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="astra-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Astra</>}</Button>
              <Button onClick={verifyAstra} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="astra-verify"><PlayCircle className="w-4 h-4 mr-1.5" /> Verify</Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
